
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import { errorHandler, AppErrorClass } from '@/utils/errorHandler';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name?: string, redirectTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const isAuthenticated = !!user && !!session;

  // Validación simple de sesión
  const validateSession = (session: Session | null): boolean => {
    if (!session) return false;
    
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    
    return expiresAt > now;
  };

  useEffect(() => {
    let isSubscriptionActive = true;
    let initialCheckComplete = false;

    // Configurar listener de cambios de auth - OPTIMIZED: Minimal logging
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isSubscriptionActive) return;
        
        const isValidSession = validateSession(session);
        
        setSession(isValidSession ? session : null);
        setUser(isValidSession && session ? session.user : null);
        
        // Solo establecer isLoading false si la verificación inicial ya se completó
        if (initialCheckComplete) {
          setIsLoading(false);
        }
      }
    );

    // Verificar sesión inicial - OPTIMIZED: Faster check
    const checkInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const isValidSession = validateSession(session);
        
        setSession(isValidSession ? session : null);
        setUser(isValidSession && session ? session.user : null);
      } catch (error) {
        // Silent error - set loading false
      } finally {
        initialCheckComplete = true;
        setIsLoading(false);
      }
    };

    checkInitialSession();

    return () => {
      isSubscriptionActive = false;
      subscription.unsubscribe();
    };
  }, []);

  const refreshSession = async (): Promise<boolean> => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();

      if (error) {
        // Fallback: try to get session again
        const { data: { session: fallbackSession } } = await supabase.auth.getSession();
        if (fallbackSession && validateSession(fallbackSession)) {
          setSession(fallbackSession);
          setUser(fallbackSession.user);
          return true;
        }
        return false;
      }

      if (session && validateSession(session)) {
        setSession(session);
        setUser(session.user);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      // Clear any existing invalid session first
      await supabase.auth.signOut();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new AppErrorClass(error.message, {
          code: 'LOGIN_ERROR',
          component: 'AuthContext',
          action: 'login'
        });
      }

      if (data.user && data.session && validateSession(data.session)) {
        // Update state immediately for better UX
        setSession(data.session);
        setUser(data.user);
        
        toast({
          title: "Bienvenido",
          description: "Has iniciado sesión correctamente.",
        });
      } else {
        throw new AppErrorClass('Login exitoso pero datos de sesión inválidos', {
          code: 'INVALID_SESSION',
          component: 'AuthContext'
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  const signup = async (email: string, password: string, name?: string, redirectTo?: string) => {
    try {
      const currentOrigin = window.location.origin;
      const finalRedirectTo = redirectTo || `${currentOrigin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: finalRedirectTo,
          data: name ? { display_name: name } : undefined,
        },
      });

      if (error) {
        throw new AppErrorClass(error.message, {
          code: 'SIGNUP_ERROR',
          component: 'AuthContext',
          action: 'signup'
        });
      }

      if (!data.session) {
        toast({
          title: "Cuenta creada",
          description: "Revisa tu email para confirmar tu cuenta.",
        });
      } else {
        toast({
          title: "Bienvenido",
          description: "Tu cuenta ha sido creada exitosamente.",
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Only attempt logout if there's actually a session
      if (!session) {
        setSession(null);
        setUser(null);
        localStorage.removeItem('sb-sbctnrmwmwnlameidnef-auth-token');
        return;
      }

      const { error } = await supabase.auth.signOut();

      // Ignore "session_not_found" errors as the session is already invalid
      if (error && !error.message.includes('session_not_found')) {
        throw new AppErrorClass(error.message, {
          code: 'LOGOUT_ERROR',
          component: 'AuthContext',
          action: 'logout'
        });
      }
      
      // Clear state immediately
      setSession(null);
      setUser(null);
      
      // Clear cached auth data
      localStorage.removeItem('sb-sbctnrmwmwnlameidnef-auth-token');
      
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión correctamente.",
      });
    } catch (error: any) {
      // Even if logout fails, clear local state
      setSession(null);
      setUser(null);
      localStorage.removeItem('sb-sbctnrmwmwnlameidnef-auth-token');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const resetUrl = `${window.location.origin}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetUrl,
      });

      if (error) {
        throw new AppErrorClass(error.message, {
          code: 'RESET_ERROR',
          component: 'AuthContext',
          action: 'resetPassword'
        });
      }
      
      toast({
        title: "Email enviado",
        description: "Revisa tu email para restablecer tu contraseña.",
      });
    } catch (error: any) {
      throw error;
    }
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated,
    login,
    signup,
    logout,
    resetPassword,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
